import React, { useEffect, useMemo, useState } from 'react'
import {
  Activity,
  Buildings,
  Calendar,
  Clock,
  EyeSlash,
  Send2,
  User,
  Warning2,
} from 'iconsax-react'
import styled, { createGlobalStyle, css, keyframes } from 'styled-components'

import {
  PARTICIPANT_STATUSES_LABELS,
  SIMULATION_STATUSES_VALUES,
} from 'containers/SpearPhishingAgent/constants'

import { Table } from 'components/lib/DesignSystem/V3'
import Button from 'components/lib/DesignSystem/V3/Button'
import Combobox from 'components/lib/DesignSystem/V3/Combobox'
import Drawer from 'components/lib/DesignSystem/V3/Drawer'
import Skeleton from 'components/lib/DesignSystem/V3/Skeleton'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import { formatDayDate, formatFullDate, sortParticipants } from '../../../../utils'
import EmailPreviewBody, { EmailPreviewFooter } from '../EmailPreview'

import SimulationDrawerHeader from './SimulationDrawerHeader'

export const DRAWER_VIEWS = {
  DETAILS: 'details',
  PREVIEW: 'preview',
}

const DRAWER_CLASS_NAME = 'psima-simulation-drawer'
const SKELETON_ROW_COUNT = 4

/* Same scoped overrides the incidents drawer applies: a softer backdrop than the global
   overlay token, and in dark mode the accent shifts to blue-400 across the whole drawer. */
const DrawerScope = createGlobalStyle`
  .ds-v3-drawer.${DRAWER_CLASS_NAME} {
    --color-overlay: rgba(47, 53, 74, 0.32);
  }
  html[data-theme='dark'] .ds-v3-drawer.${DRAWER_CLASS_NAME} {
    --color-overlay: rgba(0, 0, 0, 0.45);
    --primary: var(--t-colors-blue-400);
  }
  /* The drawer portals to body, outside the page root that re-asserts Poppins over the
     app-wide Figtree rule in Typography.js, so the same page-scoped fix is needed here. */
  .ds-v3-drawer.${DRAWER_CLASS_NAME},
  .ds-v3-drawer.${DRAWER_CLASS_NAME} * {
    font-family: var(--t-typography-font-family-font-family-default);
  }
  /* Only the top corner is rounded; the panel runs straight into the bottom edge. */
  .ds-v3-drawer.${DRAWER_CLASS_NAME} .ds-v3-drawer__panel {
    border-radius: 20px 0 0 0;
  }
`

/* The flush body is the grey page surface; everything on it is a white card. */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-4);
  padding: var(--t-spacing-4) var(--t-spacing-5) var(--t-spacing-5);
`

/* MOTION (TBD proposal): the frame stays, the content arrives. Stepping with the arrows
   brings the next simulation or employee in from the arrow's direction, 8px and 200ms on
   ease-enter; a plain click just fades. The chrome above never moves. */
const contentEnter = (offset) => keyframes`
  from {
    opacity: 0;
    transform: translateY(${offset}px);
  }
`

const Slide = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-4);
  animation: ${({ $direction }) => contentEnter($direction * 8)} 200ms cubic-bezier(0, 0, 0.2, 1)
    backwards;

  ${({ $direction }) =>
    $direction === 0 &&
    css`
      animation-duration: 150ms;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Card = styled.section`
  background: var(--color-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--t-borders-radius-rounded-2xl);
  /* clip, not hidden: hidden makes the card the sticky containing block, and the table's
     sticky header would then never pin to the drawer's scroll. */
  overflow: clip;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--t-spacing-3);
  padding: var(--t-spacing-4) var(--t-spacing-5) 0;
`

const CardTitle = styled.h3`
  margin: 0;
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-semi-bold);
  color: var(--color-text);
`

const CardCount = styled.span`
  margin-left: var(--t-spacing-1-5);
  font-weight: var(--t-typography-font-weight-regular);
  color: var(--color-text-secondary);
`

/* Two rows of three (Natali, 3.9): the simulation's dates first, then who it went to and
   who sent it. The employee count is not repeated here; it heads the Participants card. */
const DetailsRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-5);
  padding: var(--t-spacing-4) var(--t-spacing-5) var(--t-spacing-5);
`

const DetailsRow = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--t-spacing-4) var(--t-spacing-6);
  margin: 0;
`

/* Icon in a quiet square on the left, label over value on the right. Neutral grey rather
   than the dashboard's blue metric tint, and 24px, so the glyph supports the fact instead
   of competing with it (Stav, 3.9). */
const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--t-spacing-3);
  min-width: 0;
`

const DetailIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--t-spacing-6);
  height: var(--t-spacing-6);
  border-radius: var(--t-border-radius-md);
  background: var(--t-opacity-neutral-10, var(--color-surface-foreground));
  color: var(--color-text-secondary);
`

const DetailText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-0-5);
  min-width: 0;
`

const DetailLabel = styled.dt`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-1);
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-regular);
  color: var(--color-text-secondary);
`

const DetailValue = styled.dd`
  margin: 0;
  font-size: var(--t-typography-font-size-sm);
  color: ${({ $expired }) => ($expired ? 'var(--negative-color)' : 'var(--color-text)')};
  overflow-wrap: anywhere;
`

/* One detail: the tooltip, when given, wraps the whole item so the hint is reachable from
   the label and the value alike. */
const Detail = ({ Icon, label, children, tooltip, tooltipId, testId, expired }) => {
  const item = (
    <DetailItem>
      <DetailIcon aria-hidden="true">
        <Icon size={14} variant="Outline" />
      </DetailIcon>
      <DetailText>
        <DetailLabel>{label}</DetailLabel>
        <DetailValue $expired={expired} data-testid={testId}>
          {children}
        </DetailValue>
      </DetailText>
    </DetailItem>
  )

  if (!tooltip) return item

  return (
    <Tooltip id={tooltipId} side="top" arrow content={tooltip}>
      <span style={{ display: 'inline-flex', minWidth: 0 }}>{item}</span>
    </Tooltip>
  )
}

/* The two filters sit in the participants card header, right of the title. */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-2);
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;
`

const FilterSlot = styled.div`
  width: 180px;
`

const NoMatches = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--t-spacing-2);
  padding: var(--t-spacing-6) var(--t-spacing-5);
  text-align: center;
  font-size: var(--t-typography-font-size-sm);
  color: var(--color-text-secondary);
`

/* One quiet line for "something here did not load": icon, sentence, and a text action. */
const InlineNotice = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-2);
  padding: var(--t-spacing-4) var(--t-spacing-5);
  font-size: var(--t-typography-font-size-sm);
  color: var(--color-text-secondary);

  svg {
    flex-shrink: 0;
  }
`

const InlineAction = styled.button`
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  font-weight: var(--t-typography-font-weight-medium);
  color: var(--primary);

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`

const EFFECTIVE_UNTIL_HINT =
  'After this date the simulation stops collecting engagement: clicks and reports are no longer counted.'

/* The table sits inside the card with a gutter on every side, and scrolls inside it if a
   narrow drawer squeezes the columns. Rows open the email review, so they show a pointer. */
/* Sticky column headers: the drawer body is the scroll container, so the header row pins
   under the drawer chrome (52px) once the roster is longer than the viewport. Sticky only
   works while this box does not scroll itself, so horizontal scrolling is kept for narrow
   viewports only, where the table would otherwise not fit. */
const DRAWER_HEADER_HEIGHT = '52px'

const TableScroll = styled.div`
  margin-top: var(--t-spacing-3);
  padding: 0 var(--t-spacing-4) var(--t-spacing-4);

  @media (max-width: 1100px) {
    overflow-x: auto;
  }

  thead th {
    position: sticky;
    top: ${DRAWER_HEADER_HEIGHT};
    z-index: 2;
    background: var(--color-elevated);
  }

  /* A row whose email is not ready yet does not open anything, so it does not invite. */
  tbody tr.is-pending {
    cursor: default;
  }
  tbody tr.is-pending:hover {
    background-color: inherit;
  }

  table {
    width: 100%;
    /* Below this the fixed shares squeeze dates into the eye column; scroll instead. */
    min-width: 880px;
    table-layout: fixed;
    border: 1px solid var(--color-border);
    border-radius: var(--t-border-radius-lg);
  }
`

const EmailCard = styled(Card)`
  padding: var(--t-spacing-5);
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-4);
`

/**
 * One drawer, two views.
 *
 * Opening a row shows the simulation: its details card, then the participants table.
 * A participant row (or its eye) swaps this same drawer to the email that person received;
 * the drawer stays open, only its contents change.
 */
const SimulationDrawer = ({
  open,
  view,
  onClose,
  simulation,
  participants,
  participantsLoaded,
  participantsError = false,
  onRetryParticipants,
  listNavigationError = false,
  onRetryListNavigation,
  previewError = false,
  onRetryPreview,
  columns,
  clickedRow,
  activeParticipant,
  onNavigateParticipant,
  onNavigateSimulation,
  hasPrevSimulation,
  hasNextSimulation,
  onBackToDetails,
  navDirection = 0,
  onPreviewRow,
  isValidUntilExpired,
}) => {
  const isPreview = view === DRAWER_VIEWS.PREVIEW

  /* Skeleton rows keep the table's own columns, so the loading state has the shape of
     what is about to appear rather than a spinner. */
  const skeletonColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        sortable: false,
        render: () => (
          <Skeleton variant="title" width={column.key === 'previewIcon' ? '16px' : '70%'} />
        ),
      })),
    [columns]
  )
  const skeletonRows = useMemo(
    () =>
      Array.from(
        { length: Math.min(simulation?.participantsCount || SKELETON_ROW_COUNT, 8) },
        (_, index) => ({ id: `skeleton-${index}` })
      ),
    [simulation?.participantsCount]
  )

  const participantsCount = participantsLoaded ? participants.length : simulation?.participantsCount

  /* Sorting and filtering are local to the drawer; the roster is small and already here.
     No free-text search: the roster rarely passes a few dozen people (Stav, 3.9). */
  const [sort, setSort] = useState({ key: null, direction: null })
  const [statusFilter, setStatusFilter] = useState([])
  const [departmentFilter, setDepartmentFilter] = useState([])

  /* Filters and sort belong to one simulation. Carrying them over to the next one showed
     a roster "with no matches" for people who were simply never filtered. */
  const clearFilters = () => {
    setStatusFilter([])
    setDepartmentFilter([])
  }
  useEffect(() => {
    setSort({ key: null, direction: null })
    setStatusFilter([])
    setDepartmentFilter([])
  }, [simulation?.id])

  const statusOptions = useMemo(
    () => Object.entries(PARTICIPANT_STATUSES_LABELS).map(([value, label]) => ({ value, label })),
    []
  )
  const departments = simulation?.departments || []
  const departmentsLabel = departments.join(', ')
  const departmentOptions = departments.map((department) => ({
    value: department,
    label: department,
  }))

  const visibleParticipants = useMemo(() => {
    const filtered = participants.filter((participant) => {
      if (statusFilter.length && !statusFilter.includes(participant.status)) return false
      if (departmentFilter.length && !departmentFilter.includes(participant.department)) {
        return false
      }
      return true
    })
    return sortParticipants(filtered, sort)
  }, [departmentFilter, participants, sort, statusFilter])

  const hasFilters = Boolean(statusFilter.length || departmentFilter.length)
  const isSending = simulation?.status === SIMULATION_STATUSES_VALUES.SENDING
  const hasPendingEmails =
    participantsLoaded && participants.some(({ isEmailReady }) => !isEmailReady)

  return (
    <>
      <DrawerScope />
      <Drawer
        open={open}
        onClose={onClose}
        className={DRAWER_CLASS_NAME}
        variant="flush"
        showCloseButton={false}
        bodyBottomShadow
        resizable
        minWidth={680}
        /* 80vw, but on very wide screens only as wide as the content needs to stay on one
           line; past 1200px the table would just stretch its gaps. */
        width="min(80vw, 1200px)"
        footer={
          isPreview && !previewError ? (
            <EmailPreviewFooter activeParticipant={activeParticipant} />
          ) : null
        }
      >
        <SimulationDrawerHeader
          isPreview={isPreview}
          simulation={simulation}
          participants={participants}
          participantsLoaded={participantsLoaded}
          hasPrevSimulation={hasPrevSimulation}
          hasNextSimulation={hasNextSimulation}
          onNavigateSimulation={onNavigateSimulation}
          activeParticipant={activeParticipant}
          onNavigateParticipant={onNavigateParticipant}
          onBackToDetails={onBackToDetails}
          onClose={onClose}
        />
        <Body>
          <Slide
            key={`${isPreview ? 'preview' : 'details'}:${simulation?.id}:${
              isPreview ? activeParticipant?.id : ''
            }`}
            $direction={navDirection}
          >
            {isPreview ? (
              <EmailCard data-testid="Psima_SimulationDrawer_Email_div">
                {previewError ? (
                  <InlineNotice data-testid="Psima_EmailPreview_LoadFailed_div">
                    <Warning2 size={16} variant="Outline" />
                    <span>Couldn&apos;t load this email.</span>
                    <InlineAction
                      type="button"
                      id="Psima_SimulationDrawer_RetryPreview_button"
                      data-testid="Psima_SimulationDrawer_RetryPreview_button"
                      onClick={() => onRetryPreview?.(activeParticipant)}
                    >
                      Try again
                    </InlineAction>
                  </InlineNotice>
                ) : (
                  <EmailPreviewBody />
                )}
              </EmailCard>
            ) : (
              <Stack data-testid={`Psima_SimulationDrawer_Details_div-${simulation?.id}`}>
                {listNavigationError && (
                  <InlineNotice data-testid="Psima_SimulationDrawer_ListNavigationError_div">
                    <Warning2 size={16} variant="Outline" />
                    <span>Couldn&apos;t load the next page of simulations.</span>
                    <InlineAction
                      type="button"
                      id="Psima_SimulationDrawer_RetryList_button"
                      data-testid="Psima_SimulationDrawer_RetryList_button"
                      onClick={onRetryListNavigation}
                    >
                      Try again
                    </InlineAction>
                  </InlineNotice>
                )}
                <Card>
                  <CardHeader>
                    <CardTitle>Simulation details</CardTitle>
                  </CardHeader>
                  <DetailsRows>
                    <DetailsRow>
                      <Detail Icon={Send2} label="Launched at">
                        {formatFullDate(simulation?.createdAt) || '–'}
                      </Detail>
                      <Detail
                        Icon={Calendar}
                        label="Effective until"
                        tooltip={EFFECTIVE_UNTIL_HINT}
                        tooltipId="Psima_SimulationDrawer_EffectiveUntil_tooltip"
                        expired={isValidUntilExpired(simulation?.validUntil)}
                      >
                        {formatFullDate(simulation?.validUntil) || '–'}
                      </Detail>
                      <Detail
                        Icon={Activity}
                        label="Last engagement"
                        testId={`PsimaLaunched_LastEngagement_div-${simulation?.id}`}
                      >
                        {simulation?.lastEngagementAt
                          ? formatDayDate(simulation.lastEngagementAt)
                          : 'No engagement yet'}
                      </Detail>
                    </DetailsRow>
                    <DetailsRow>
                      <Detail
                        Icon={Buildings}
                        label="Departments"
                        testId={`PsimaLaunched_Department_div-${simulation?.id}`}
                      >
                        {departmentsLabel || '–'}
                      </Detail>
                      <Detail Icon={EyeSlash} label="No engagement">
                        {`${simulation?.engagement?.noEngagement ?? 0}%`}
                      </Detail>
                      <Detail Icon={User} label="Launched by">
                        {simulation?.launchedBy || '–'}
                      </Detail>
                    </DetailsRow>
                  </DetailsRows>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>
                      Participants
                      {participantsCount != null && <CardCount>{participantsCount}</CardCount>}
                    </CardTitle>
                    {participantsLoaded && participants.length > 0 && (
                      <Toolbar>
                        <FilterSlot>
                          <Combobox
                            id="Psima_SimulationDrawer_Status_Combobox"
                            dataTestId="Psima_SimulationDrawer_Status_Combobox"
                            multiple
                            /* The popup portals to body at the dropdown layer, which sits
                             under the drawer; lift it like the incidents drawer does. */
                            popupZIndex="var(--z-index-modal-x)"
                            selectAllLabel="All statuses"
                            placeholder="Status"
                            options={statusOptions}
                            value={statusFilter}
                            onChange={setStatusFilter}
                          />
                        </FilterSlot>
                        <FilterSlot>
                          <Combobox
                            id="Psima_SimulationDrawer_Department_Combobox"
                            dataTestId="Psima_SimulationDrawer_Department_Combobox"
                            multiple
                            /* The popup portals to body at the dropdown layer, which sits
                             under the drawer; lift it like the incidents drawer does. */
                            popupZIndex="var(--z-index-modal-x)"
                            selectAllLabel="All departments"
                            placeholder="Department"
                            options={departmentOptions}
                            value={departmentFilter}
                            onChange={setDepartmentFilter}
                            emptyText="No departments"
                          />
                        </FilterSlot>
                      </Toolbar>
                    )}
                  </CardHeader>
                  {participantsError && (
                    <InlineNotice data-testid="Psima_SimulationDrawer_ParticipantsError_div">
                      <Warning2 size={16} variant="Outline" />
                      <span>Couldn&apos;t load the participants for this simulation.</span>
                      <InlineAction
                        type="button"
                        id="Psima_SimulationDrawer_RetryParticipants_button"
                        data-testid="Psima_SimulationDrawer_RetryParticipants_button"
                        onClick={() => onRetryParticipants?.(simulation?.id)}
                      >
                        Try again
                      </InlineAction>
                    </InlineNotice>
                  )}
                  {!participantsError && (isSending || hasPendingEmails) && (
                    <InlineNotice data-testid="Psima_SimulationDrawer_Preparing_div">
                      <Clock size={16} variant="Outline" />
                      <span>
                        Emails are still being prepared. Previews open as each one is ready.
                      </span>
                    </InlineNotice>
                  )}
                  {!participantsError && (
                    <TableScroll>
                      {participantsLoaded && hasFilters && visibleParticipants.length === 0 ? (
                        <NoMatches data-testid="Psima_SimulationDrawer_NoMatches_div">
                          <span>No employees match these filters.</span>
                          <Button
                            id="Psima_SimulationDrawer_ClearFilters_button"
                            dataTestId="Psima_SimulationDrawer_ClearFilters_button"
                            variant="tertiary"
                            size="small"
                            onClick={clearFilters}
                          >
                            Clear filters
                          </Button>
                        </NoMatches>
                      ) : participantsLoaded ? (
                        <Table
                          density="comfortable"
                          columns={columns}
                          data={visibleParticipants}
                          clickedRow={clickedRow}
                          sortState={sort}
                          onSort={setSort}
                          rowClassName={(row) => (row.isEmailReady ? undefined : 'is-pending')}
                          onRowClick={(row) => row.isEmailReady && onPreviewRow(row)}
                        />
                      ) : (
                        <Table
                          density="comfortable"
                          columns={skeletonColumns}
                          data={skeletonRows}
                          data-testid="Psima_SimulationDrawer_TableSkeleton_table"
                        />
                      )}
                    </TableScroll>
                  )}
                </Card>
              </Stack>
            )}
          </Slide>
        </Body>
      </Drawer>
    </>
  )
}

export default SimulationDrawer
